import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { MFASession } from "../../services/mfa_service";
import type { IUser } from "../Users/types";
import { useNavigate } from "react-router";
import api, { stopAutoRefresh, startAutoRefresh } from "../../services/api";
import tokenService from "../../services/token_service";
import mfaService from "../../services/mfa_service";
import { toast } from "react-toastify";
import type {
  IAuthContextData,
  ICreatePatientBody,
  ICreatePatientResponse,
  IGetByCpfResponse,
  IResetPasswordParams,
  IResetPasswordResponse,
  ISendCodeBody,
  ISendCodeResponse,
  ISession,
  IUpdateInternalDoctorMySQLDataParams,
  IUpdateInternalDoctorParams,
  IUpdateInternalDoctorResponse,
} from "./types";
import { setActiveHospitalStorage } from "../ActiveHospital/utils";

export const AuthContext = createContext<IAuthContextData>(
  {} as IAuthContextData
);

// Clears everything a session leaves in this browser, exactly as "Sair" does.
// Every path that ends a session must come through here: a path that clears
// only the token leaves the active hospital (`h`) behind, and the next person
// to log in on this browser inherits the previous user's hospital.
const clearSessionStorage = () => {
  tokenService.clearTokens();
  mfaService.clearMFASession();
  localStorage.clear();
  delete api.defaults.headers.common.Authorization;
  stopAutoRefresh();
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [isExpiredTokenModalOpen, setIsExpiredTokenModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeSessions, _setActiveSessions] = useState<ISession[]>([]);

  const navigate = useNavigate();

  // ========================================
  // USER UPDATE
  // ========================================
  const updateUser = useCallback((newUser: IUser | null) => {
    if (!newUser) {
      setUser(null);
      localStorage.removeItem("auth:user");
      return;
    }

    setIsExpiredTokenModalOpen(false);
    setUser(newUser);
    localStorage.setItem("auth:user", JSON.stringify(newUser));
  }, []);

  // ========================================
  // LOGOUT
  // ========================================
  const logout = useCallback(() => {
    clearSessionStorage();
    setUser(null);

    // Redireciona para login
    navigate("/login", { replace: true });
  }, [navigate]);

  // ========================================
  // HANDLERS DE EVENTOS
  // ========================================
  const handleTokenExpired = useCallback(() => {
    console.warn("🔄 Token expirado - evento disparado");
    // Clear now, not when OK is clicked: a closed tab or a reload never
    // reaches the modal's button. The modal only informs.
    clearSessionStorage();
    setIsExpiredTokenModalOpen(true);
  }, []);

  const handleSessionRevoked = useCallback(() => {
    console.warn("🚫 Sessão revogada - evento disparado");
    toast.error("Sua sessão foi encerrada por segurança.");
    logout();
  }, [logout]);

  // Registra listeners de eventos
  useEffect(() => {
    window.addEventListener("token:expired", handleTokenExpired);
    window.addEventListener("session:revoked", handleSessionRevoked);

    return () => {
      window.removeEventListener("token:expired", handleTokenExpired);
      window.removeEventListener("session:revoked", handleSessionRevoked);
    };
  }, [handleTokenExpired, handleSessionRevoked]);

  // ========================================
  // SESSION RESTORATION (MOUNT)
  // ========================================
  useEffect(() => {
    let mounted = true;

    const restoreSession = async () => {
      try {
        const cachedToken = tokenService.getAccessToken();
        const cachedUserString = localStorage.getItem("auth:user");

        // No token — no session
        if (!cachedToken) {
          if (mounted) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }

        // Token expired — attempt refresh
        if (tokenService.isTokenExpired()) {
          try {
            const refreshResponse = await api.post(
              "/auth/refresh",
              {},
              { withCredentials: true }
            );

            const { token: newToken, user: userData, expiresAt } = refreshResponse.data;

            if (mounted && newToken) {
              tokenService.setTokens(newToken, expiresAt);
              updateUser(userData || null);
              api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
              startAutoRefresh();
            }
          } catch (refreshError) {
            console.error("❌ Erro ao fazer refresh:", refreshError);

            if (mounted) {
              clearSessionStorage();
              setUser(null);
            }
          } finally {
            if (mounted) {
              setIsLoading(false);
            }
          }
          return;
        }

        // Valid token — restore user from cache
        if (cachedUserString && mounted) {
          try {
            const cachedUser = JSON.parse(cachedUserString);
            setUser(cachedUser);
            api.defaults.headers.common.Authorization = `Bearer ${cachedToken}`;
            startAutoRefresh();
          } catch (parseError) {
            console.error("❌ Erro ao parsear user:", parseError);
            clearSessionStorage();
            setUser(null);
          }
        }
      } catch (error) {
        console.error("❌ Erro ao restaurar sessão:", error);
        if (mounted) {
          clearSessionStorage();
          setUser(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      mounted = false;
    };
  }, [updateUser]);

  // ========================================
  // REFRESH MANUAL
  // ========================================
  const refreshToken = useCallback(async () => {
    try {
      const response = await api.post(
        "/auth/refresh",
        {},
        { withCredentials: true }
      );

      const { token: newToken, user: userData, expiresAt } = response.data;

      tokenService.setTokens(newToken, expiresAt);
      if (userData) {
        updateUser(userData);
      }

      setIsExpiredTokenModalOpen(false);
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      startAutoRefresh();
    } catch (error) {
      console.error("❌ Erro ao renovar token:", error);
      logout();
    }
  }, [updateUser, logout]);

  // ========================================
  // SIGN IN
  // ========================================
  const signIn = useCallback(
    async (
      email: string,
      password: string,
      roleId: number,
      cpf?: string,
      protocol?: string,
      patientFirst: boolean = false,
      user_id?: number
    ) => {
      try {
        const response = await api.post(`/auth/login/${roleId}`, {
          email,
          password,
          cpf,
          protocol,
          patientFirst,
          user_id,
        });

        const {
          requiresMFA,
          mfaToken,
          userId,
          token,
          user: userData,
          expiresAt,
          type,
          cellphone,
          email: maskedEmail,
        } = response.data;

        // A new login never inherits the active hospital of whoever used this
        // browser before, whatever way that session ended. This covers any
        // ending path that skipped clearSessionStorage.
        setActiveHospitalStorage(undefined);

        // Fluxo MFA
        if (requiresMFA) {
          const mfaSession: MFASession = {
            mfaToken,
            userId,
            roleId,
            expiresAt,
            cellphone,
            email: maskedEmail,
          };
          mfaService.setMFASession(mfaSession);
          return { requiresMFA: true, mfaSession, status: 200, type };
        }

        // Login direto (sem MFA)
        tokenService.setTokens(token, expiresAt);
        setIsExpiredTokenModalOpen(false);
        updateUser(userData);

        // Legacy code compatibility
        localStorage.setItem("User_id_senne", String(userData?.id));

        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        startAutoRefresh();

        if (response.data.protocol) return response.data;
        return { requiresMFA: false, user: userData, status: 200, type };
      } catch (error: any) {
        toast.error(error.response?.data?.message || "Erro ao fazer login");
        throw error;
      }
    },
    [updateUser]
  );

  // ========================================
  // COMPLETE MFA
  // ========================================
  const completeMFA = useCallback(
    async (code: string, role_id: number): Promise<boolean> => {
      try {
        const result = await mfaService.verifyMFACode(code, role_id);
        if (result.success && result.token) {
          setActiveHospitalStorage(undefined);
          tokenService.setTokens(result.token);
          updateUser(result.user);
          api.defaults.headers.common.Authorization = `Bearer ${result.token}`;
          startAutoRefresh();
          return true;
        }
        return false;
      } catch (error) {
        console.error("❌ Erro ao completar MFA:", error);
        return false;
      }
    },
    [updateUser]
  );

  // ========================================
  // REFRESH USER DATA
  // ========================================
  const refreshUserData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await api.get(`/users/${user.id}`);
      updateUser(response.data.user);
    } catch (error) {
      console.error("❌ Erro ao atualizar dados do usuário:", error);
    }
  }, [user?.id, updateUser]);

  // ========================================
  // OTHER FUNCTIONS (kept backward-compatible)
  // ========================================
  const validateUser = useCallback(async (data: any) => {
    try {
      const response = await api.post("/auth/validate/user", data);
      if (response?.data?.message) toast.success(response.data.message);
      return response;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao validar usuário");
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (data: IResetPasswordParams) => {
    try {
      const response = await api.post<IResetPasswordResponse>(
        "/password/reset",
        data
      );
      if (response?.data?.message) toast.success(response.data.message);
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao resetar senha");
      throw error;
    }
  }, []);

  const updateInternalDoctor = useCallback(
    async (formToSend: IUpdateInternalDoctorParams) => {
      try {
        const response = await api.put<IUpdateInternalDoctorResponse>(
          `/internal_doctors`,
          formToSend
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message)
          toast.error(error?.response?.data?.message);
        return error?.response?.data;
      }
    },
    []
  );

  const updateInternalDoctorMySQLData = useCallback(
    async (formToSend: IUpdateInternalDoctorMySQLDataParams) => {
      try {
        const response = await api.put<IUpdateInternalDoctorResponse>(
          `/userComplement/${formToSend.user_id}`,
          formToSend
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message)
          toast.error(error?.response?.data?.message);
        return error?.response?.data;
      }
    },
    []
  );

  const updateUserLogged = useCallback(
    async (params: Partial<IUser>, userId: number) => {
      try {
        const response = await api.patch<{ user: IUser; message: string }>(
          `/users/${userId}`,
          params
        );
        toast.success(response.data.message);

        setUser((prevUser) => {
          const merged = { ...prevUser, ...response.data.user } as IUser;
          localStorage.setItem("auth:user", JSON.stringify(merged));
          return merged;
        });
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || "Erro ao atualizar usuário"
        );
      }
    },
    []
  );

  const loadActiveSessions = useCallback(async () => {
    try {
      // Implement loading logic here
    } catch (error) {
      console.error("❌ Erro ao carregar sessões:", error);
    }
  }, []);

  const revokeSession = useCallback(
    async (_sessionId: number) => {
      try {
        toast.success("Sessão revogada com sucesso");
        await loadActiveSessions();
      } catch (error: any) {
        toast.error("Erro ao revogar sessão");
      }
    },
    [loadActiveSessions]
  );

  const getByCpf = useCallback(async (cpf: string) => {
    try {
      const response = await api.get<IGetByCpfResponse>(
        `/auth/validate-identifier/${cpf}`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao validar CPF");
      throw error;
    }
  }, []);

  const createPatient = useCallback(async (body: ICreatePatientBody) => {
    try {
      const response = await api.post<ICreatePatientResponse>(
        "/auth/create-patient",
        body
      );
      localStorage.setItem("User_id_senne", response.data.id.toString());
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao criar paciente");
      throw error;
    }
  }, []);

  const sendCode = useCallback(
    async (body: ISendCodeBody) => {
      try {
        const response = await api.post<ISendCodeResponse>(
          "/auth/send-code",
          body
        );
        return response.data;
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Erro ao enviar código");
        navigate("/login");
        throw error;
      }
    },
    [navigate]
  );

  // ========================================
  // CONTEXT VALUE
  // ========================================
  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user && tokenService.hasValidToken(),
      isLoading,
      signIn,
      logout,
      validateUser,
      resetPassword,
      completeMFA,
      refreshUserData,
      isExpiredTokenModalOpen,
      setIsExpiredTokenModalOpen,
      setUser: updateUser,
      updateInternalDoctor,
      updateInternalDoctorMySQLData,
      updateUserLogged,
      activeSessions,
      loadActiveSessions,
      revokeSession,
      getByCpf,
      createPatient,
      sendCode,
      updateUser,
      refreshToken,
    }),
    [
      user,
      isLoading,
      signIn,
      logout,
      validateUser,
      resetPassword,
      completeMFA,
      refreshUserData,
      isExpiredTokenModalOpen,
      updateUser,
      updateInternalDoctor,
      updateInternalDoctorMySQLData,
      updateUserLogged,
      activeSessions,
      loadActiveSessions,
      revokeSession,
      getByCpf,
      createPatient,
      sendCode,
      refreshToken,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};